import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { requests } from 'RestAPIs/rapini';
import { showSnackbar } from 'components/shared';
import { organizationalUnitCostCenterIds } from 'utils/organizationalUnits';

import type {
  EventResponse,
  GetAppointmentsFiltered,
  PlanningEntitySelection as PlanningEntitySelectionRequest
} from 'generated/index';
import type { PlanningEntitySelection } from 'components/shared/PlanningEntityPicker';
import type { OrganizationalUnitOption } from 'utils/organizationalUnits';

import { APPOINTMENTS_OVERVIEW_MAX_PERIOD_YEARS } from '../types';

export type UseFilteredAppointmentsParams = {
  startDate: string;
  endDate: string;
  /** Who, as picked: people and plan units, combined as a union. */
  selections: PlanningEntitySelection[];
  /** The picked plan units resolved, to expand a plan unit selection to br ids. */
  organizationalUnits: OrganizationalUnitOption[];
  activityIds: string[];
};

const sorted = <T extends string | number>(values: T[]) =>
  [...values].sort((a, b) => String(a).localeCompare(String(b)));

/**
 * Whether the period, both ends inclusive, spans more than the maximum: compared by calendar day, so the
 * end-of-day time computeRange hands over does not count as a day more. 1 January to 31 December fits.
 */
export const isPeriodLongerThanMax = (startDate: string, endDate: string) =>
  !dayjs(endDate)
    .startOf('day')
    .isBefore(
      dayjs(startDate)
        .startOf('day')
        .add(APPOINTMENTS_OVERVIEW_MAX_PERIOD_YEARS, 'year')
    );

/**
 * The selections as the request carries them. A plan unit selection becomes the br ids of its plan units —
 * a plan group expanded to the plan units it holds. One that resolves to none is still sent, with no br ids:
 * the backend answers plan units covering nobody with nothing, even beside an activity. Leaving it out would
 * widen the request to every appointment of the picked activities, while the chips still name the plan unit.
 */
export const toRequestSelections = (
  selections: PlanningEntitySelection[],
  organizationalUnits: OrganizationalUnitOption[]
): PlanningEntitySelectionRequest[] =>
  selections.map<PlanningEntitySelectionRequest>((selection) => {
    if (selection.kind === 'ENTITY') {
      return { kind: 'ENTITY', planningEntity: selection.planningEntity };
    }
    const organizationalUnit = organizationalUnits.find(
      ({ uniqId }) => uniqId === selection.organizationalUnitId
    );
    return {
      kind: 'ORGANIZATIONAL_UNITS',
      organizationalUnitIds: organizationalUnit
        ? organizationalUnitCostCenterIds([organizationalUnit])
        : [],
      perspective: selection.perspective
    };
  });

/**
 * The request body for the overview's filter. The download sends the same object, so the file always
 * holds the rows on screen.
 */
export const buildAppointmentsFilter = ({
  startDate,
  endDate,
  selections,
  organizationalUnits,
  activityIds
}: UseFilteredAppointmentsParams): GetAppointmentsFiltered => ({
  startDate: dayjs(startDate).format('YYYY-MM-DD'),
  endDate: dayjs(endDate).format('YYYY-MM-DD'),
  selections: toRequestSelections(selections, organizationalUnits),
  activityIds
});

/** A request selection as a stable string, for the query key. */
const requestSelectionKey = ({
  kind,
  planningEntity,
  organizationalUnitIds = [],
  perspective
}: PlanningEntitySelectionRequest) =>
  kind === 'ENTITY'
    ? `ENTITY:${planningEntity?.planningEntityType}:${planningEntity?.entityId}`
    : `ORGANIZATIONAL_UNITS:${perspective}:${sorted(organizationalUnitIds).join(
        ','
      )}`;

export const useFilteredAppointments = ({
  startDate,
  endDate,
  selections,
  organizationalUnits,
  activityIds
}: UseFilteredAppointmentsParams) => {
  const { t } = useTranslation('common');

  const requestSelections = toRequestSelections(
    selections,
    organizationalUnits
  );

  // Some filter has to name something: a request that names nothing would ask for every appointment of
  // the tenant. The period bounds the rest, whichever filter is set.
  const namesSomething = requestSelections.length > 0 || activityIds.length > 0;
  const isPeriodTooLong =
    !!startDate && !!endDate && isPeriodLongerThanMax(startDate, endDate);
  const enabled =
    namesSomething && !isPeriodTooLong && !!startDate && !!endDate;

  const query = useQuery<EventResponse[]>(
    [
      'appointmentsOverview',
      startDate,
      endDate,
      sorted(requestSelections.map(requestSelectionKey)),
      sorted(activityIds)
    ],
    async () => {
      if (!enabled) return [];

      return await requests.getAppointmentFiltered(
        buildAppointmentsFilter({
          startDate,
          endDate,
          selections,
          organizationalUnits,
          activityIds
        })
      );
    },
    {
      enabled,
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      },
      refetchOnWindowFocus: false
    }
  );

  return { ...query, namesSomething, isPeriodTooLong };
};
